import { useParams, useNavigate, Link } from "react-router-dom";
import {
    Card,
    CardBody,
    CardHeader,
    Chip,
    Button,
    Skeleton,
} from "@heroui/react";
import { LuArrowLeft, LuPencil, LuPrinter } from "react-icons/lu";
import { useFetchData } from "@/hooks";
import {
    PageHeader,
    ThemedText,
    ErrorState,
    EmptyState,
} from "@/components/ui";
import { formatCurrency, formatDate, formatThousands } from "@/utils";
import { useUser } from "@/context/UserContext";

const PurchaseOrderDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { hasPermission } = useUser();

    const { data, isLoading, error, refetch } = useFetchData(
        `purchase-orders/${id}/show`,
    );

    const payments = data?.payments || [];

    if (error) {
        const is404 = error?.response?.status === 404;
        return (
            <div className="p-6">
                {is404 ? (
                    <EmptyState
                        title="Data Tidak Ditemukan"
                        description="Purchase order yang Anda cari tidak ditemukan atau sudah dihapus."
                        actionLabel="Kembali ke Daftar"
                        onAction={() => navigate("/purchase-order")}
                    />
                ) : (
                    <ErrorState
                        title="Gagal Memuat Detail Pembelian"
                        message={error?.message}
                        onRetry={refetch}
                    />
                )}
            </div>
        );
    }

    const order = data;
    const items = order?.items || [];

    // Calculate grand total from items
    const grandTotal = items.reduce((sum, item) => {
        const subtotal = item.quantity * item.price - (item.discount || 0);
        return sum + subtotal;
    }, 0);

    return (
        <div className="flex h-full w-full flex-col gap-4">
            {/* Page Header */}
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <PageHeader
                    title="Detail Pembelian"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Pembelian", href: "/purchase-order" },
                        isLoading
                            ? { label: "..." }
                            : { label: order?.order_number },
                    ]}
                />
                <div className="flex items-center gap-2">
                    <Button
                        variant="flat"
                        size="sm"
                        startContent={<LuArrowLeft />}
                        onPress={() => navigate("/purchase-order")}
                    >
                        Kembali
                    </Button>
                    <Button
                        variant="flat"
                        size="sm"
                        startContent={<LuPrinter />}
                        onPress={() => navigate(`/purchase-order/${id}/print`)}
                    >
                        Print
                    </Button>
                    {hasPermission("purchase-order.edit") && (
                        <Button
                            as={Link}
                            to={`/purchase-order/${id}/edit`}
                            color="primary"
                            size="sm"
                            startContent={<LuPencil />}
                        >
                            Edit
                        </Button>
                    )}
                </div>
            </div>

            {/* Info Order */}
            <Card className="border-default-200 border" shadow="none">
                <CardHeader className="px-4 pt-4 pb-0">
                    <ThemedText size="md" className="font-semibold">
                        Informasi Order
                    </ThemedText>
                </CardHeader>
                <CardBody className="p-4">
                    {isLoading ? (
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {[...Array(8)].map((_, i) => (
                                <div key={i} className="flex flex-col gap-1">
                                    <Skeleton className="h-4 w-24 rounded-lg" />
                                    <Skeleton className="h-5 w-40 rounded-lg" />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                <InfoItem
                                    label="No. Order"
                                    value={order?.order_number}
                                />
                                <InfoItem
                                    label="Supplier"
                                    value={order?.supplier?.name || "-"}
                                />
                                <InfoItem
                                    label="Tanggal Order"
                                    value={formatDate(order?.order_date)}
                                />
                                <InfoItem
                                    label="Jatuh Tempo"
                                    value={
                                        order?.due_date
                                            ? formatDate(order.due_date)
                                            : "Tanpa jatuh tempo"
                                    }
                                />
                                <InfoItem
                                    label="Metode Pembayaran"
                                    value={order?.payment_method?.name || "-"}
                                />
                                <div className="flex flex-col gap-1">
                                    <ThemedText
                                        size="xs"
                                        variant="secondary"
                                        className="font-medium"
                                    >
                                        Status Pembayaran
                                    </ThemedText>
                                    <div>
                                        <Chip
                                            color={
                                                order?.payment_status
                                                    ?.badge_color || "default"
                                            }
                                            size="sm"
                                            variant="flat"
                                            className="font-medium"
                                        >
                                            {order?.payment_status?.name ||
                                                "Unknown"}
                                        </Chip>
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <ThemedText
                                        size="xs"
                                        variant="secondary"
                                        className="font-medium"
                                    >
                                        Status Order
                                    </ThemedText>
                                    <div>
                                        <Chip
                                            color={
                                                order?.order_status
                                                    ?.badge_color || "default"
                                            }
                                            size="sm"
                                            variant="flat"
                                            className="font-medium"
                                        >
                                            {order?.order_status?.name ||
                                                "Unknown"}
                                        </Chip>
                                    </div>
                                </div>
                                <InfoItem
                                    label="Total"
                                    value={formatCurrency(order?.total_amount)}
                                    bold
                                />
                                {order?.description && (
                                    <InfoItem
                                        label="Keterangan"
                                        value={order.description}
                                    />
                                )}
                            </div>
                        </>
                    )}
                </CardBody>
            </Card>

            {/* Daftar Item */}
            <Card className="border-default-200 border" shadow="none">
                <CardHeader className="flex flex-col items-start gap-0.5 px-4 pt-4">
                    <ThemedText size="md" className="font-semibold">
                        Daftar Item
                    </ThemedText>
                    {!isLoading && (
                        <ThemedText size="xs" variant="caption">
                            {items.length} item
                        </ThemedText>
                    )}
                </CardHeader>
                <CardBody className="overflow-x-auto p-4 pt-2">
                    {isLoading ? (
                        <div className="flex flex-col gap-3">
                            {[...Array(4)].map((_, i) => (
                                <div
                                    key={i}
                                    className="flex w-full items-center gap-4"
                                >
                                    <Skeleton className="h-5 w-8 shrink-0 rounded-lg" />
                                    <Skeleton className="h-5 flex-3 rounded-lg" />
                                    <Skeleton className="h-5 flex-1 rounded-lg" />
                                    <Skeleton className="h-5 flex-1 rounded-lg" />
                                    <Skeleton className="h-5 flex-[1.5] rounded-lg" />
                                    <Skeleton className="h-5 flex-1 rounded-lg" />
                                    <Skeleton className="h-5 flex-[1.5] rounded-lg" />
                                    <Skeleton className="h-5 flex-[1.5] rounded-lg" />
                                </div>
                            ))}
                        </div>
                    ) : items.length === 0 ? (
                        <ThemedText
                            size="sm"
                            variant="secondary"
                            className="py-8 text-center"
                        >
                            Tidak ada item.
                        </ThemedText>
                    ) : (
                        <table className="themed-table">
                            <thead>
                                <tr>
                                    <th>No.</th>
                                    <th>Barang</th>
                                    <th>Gudang</th>
                                    <th>Satuan</th>
                                    <th>Jumlah</th>
                                    <th>Harga</th>
                                    <th>Diskon</th>
                                    <th>Subtotal</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item, index) => {
                                    const subtotal =
                                        item.quantity * item.price -
                                        (item.discount || 0);

                                    return (
                                        <tr key={item.id}>
                                            <td className="text-center">
                                                <ThemedText size="xs">
                                                    {index + 1}.
                                                </ThemedText>
                                            </td>
                                            <td>
                                                <ThemedText size="xs">
                                                    {item.material?.name || "-"}
                                                </ThemedText>
                                            </td>
                                            <td className="text-center">
                                                <ThemedText size="xs">
                                                    {item.warehouse?.name ||
                                                        "-"}
                                                </ThemedText>
                                            </td>
                                            <td className="text-center">
                                                <ThemedText size="xs">
                                                    {item.unit?.name || "-"}
                                                </ThemedText>
                                            </td>
                                            <td className="text-right">
                                                <ThemedText size="xs">
                                                    {formatThousands(
                                                        item.quantity,
                                                    )}
                                                </ThemedText>
                                            </td>
                                            <td className="text-right">
                                                <ThemedText size="xs">
                                                    {formatCurrency(item.price)}
                                                </ThemedText>
                                            </td>
                                            <td className="text-right">
                                                <ThemedText size="xs">
                                                    {formatCurrency(
                                                        item.discount || 0,
                                                    )}
                                                </ThemedText>
                                            </td>
                                            <td className="text-right">
                                                <ThemedText size="xs">
                                                    {formatCurrency(subtotal)}
                                                </ThemedText>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan={7} className="text-right">
                                        <ThemedText
                                            size="xs"
                                            className="font-bold"
                                        >
                                            TOTAL
                                        </ThemedText>
                                    </td>
                                    <td className="text-right">
                                        <ThemedText
                                            size="xs"
                                            className="font-bold"
                                        >
                                            {formatCurrency(grandTotal)}
                                        </ThemedText>
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    )}
                </CardBody>
            </Card>

            {/* Riwayat Pembayaran */}
            <Card className="border-default-200 border" shadow="none">
                <CardHeader className="flex flex-col items-start gap-0.5 px-4 pt-4">
                    <ThemedText size="md" className="font-semibold">
                        Riwayat Pembayaran
                    </ThemedText>
                    {!isLoading && (
                        <ThemedText size="xs" variant="caption">
                            {payments.length} transaksi
                        </ThemedText>
                    )}
                </CardHeader>
                <CardBody className="overflow-x-auto p-4 pt-2">
                    {isLoading ? (
                        <div className="flex flex-col gap-3">
                            {[...Array(2)].map((_, i) => (
                                <div
                                    key={i}
                                    className="flex w-full items-center gap-4"
                                >
                                    <Skeleton className="h-5 flex-1 rounded-lg" />
                                    <Skeleton className="h-5 flex-1 rounded-lg" />
                                    <Skeleton className="h-5 flex-1 rounded-lg" />
                                    <Skeleton className="h-5 flex-1 rounded-lg" />
                                    <Skeleton className="h-5 flex-1 rounded-lg" />
                                </div>
                            ))}
                        </div>
                    ) : !payments || payments.length === 0 ? (
                        <ThemedText
                            size="sm"
                            variant="secondary"
                            className="py-4 text-center"
                        >
                            Belum ada pembayaran.
                        </ThemedText>
                    ) : (
                        <table className="themed-table">
                            <thead>
                                <tr>
                                    <th>No. Pembayaran</th>
                                    <th>Tanggal Bayar</th>
                                    <th>Kas / Bank</th>
                                    <th>Keterangan</th>
                                    <th className="text-right">Diskon</th>
                                    <th className="text-right">Jumlah Bayar</th>
                                </tr>
                            </thead>
                            <tbody>
                                {payments.map((p) => (
                                    <tr key={p.id}>
                                        <td>
                                            <ThemedText
                                                size="xs"
                                                className="font-medium"
                                            >
                                                {p.payment_number || "-"}
                                            </ThemedText>
                                        </td>
                                        <td>
                                            <ThemedText size="xs">
                                                {p.payment_date
                                                    ? formatDate(
                                                          p.payment_date,
                                                          "dd MMM yyyy",
                                                      )
                                                    : "-"}
                                            </ThemedText>
                                        </td>
                                        <td>
                                            <ThemedText size="xs">
                                                {p.master_cash || "-"}
                                            </ThemedText>
                                        </td>
                                        <td>
                                            <ThemedText
                                                size="xs"
                                                variant="secondary"
                                            >
                                                {p.description || "-"}
                                            </ThemedText>
                                        </td>
                                        <td className="text-right">
                                            <ThemedText
                                                size="xs"
                                                className={
                                                    p.discount > 0
                                                        ? "text-danger"
                                                        : ""
                                                }
                                            >
                                                {p.discount > 0
                                                    ? formatCurrency(p.discount)
                                                    : "-"}
                                            </ThemedText>
                                        </td>
                                        <td className="text-right">
                                            <ThemedText
                                                size="xs"
                                                className="font-medium"
                                            >
                                                {formatCurrency(p.amount)}
                                            </ThemedText>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan={5} className="text-right">
                                        <ThemedText
                                            size="xs"
                                            className="font-bold"
                                        >
                                            TOTAL
                                        </ThemedText>
                                    </td>
                                    <td className="text-right">
                                        <ThemedText
                                            size="xs"
                                            className="font-bold"
                                        >
                                            {formatCurrency(
                                                payments.reduce(
                                                    (s, p) =>
                                                        s +
                                                        parseFloat(
                                                            p.amount || 0,
                                                        ),
                                                    0,
                                                ),
                                            )}
                                        </ThemedText>
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    )}
                </CardBody>
            </Card>

            {/* Footer */}
            {!isLoading && order && (
                <div className="flex flex-col gap-3 px-1">
                    {/* Metadata */}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                        <ThemedText size="xs" variant="secondary">
                            Dibuat:{" "}
                            {formatDate(order.created_at, {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                            })}
                        </ThemedText>
                        <ThemedText size="xs" variant="secondary">
                            ·
                        </ThemedText>
                        <ThemedText size="xs" variant="secondary">
                            Terakhir diubah:{" "}
                            {formatDate(order.updated_at, {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                            })}
                        </ThemedText>
                    </div>

                    {/* Syarat & Ketentuan */}
                    <div className="space-y-1 leading-relaxed">
                        <ThemedText
                            size="xs"
                            variant="secondary"
                            className="font-medium"
                        >
                            Syarat & Ketentuan:
                        </ThemedText>
                        <ol className="text-default-400 marker:text-default-400 list-disc space-y-0.5 pl-4">
                            <li>
                                <ThemedText size="xs" variant="secondary">
                                    Barang yang sudah diterima harus diperiksa
                                    kondisinya sebelum ditandatangani.
                                </ThemedText>
                            </li>
                            <li>
                                <ThemedText size="xs" variant="secondary">
                                    Pembayaran dilakukan sesuai dengan metode
                                    dan tenggat waktu yang telah disepakati.
                                </ThemedText>
                            </li>
                            <li>
                                <ThemedText size="xs" variant="secondary">
                                    Harga yang tertera sudah termasuk
                                    potongan/diskon yang berlaku.
                                </ThemedText>
                            </li>
                            <li>
                                <ThemedText size="xs" variant="secondary">
                                    Klaim atas kerusakan atau ketidaksesuaian
                                    barang harus diajukan maksimal 1x24 jam
                                    setelah barang diterima.
                                </ThemedText>
                            </li>
                        </ol>
                    </div>
                </div>
            )}
        </div>
    );
};

const InfoItem = ({ label, value, bold = false }) => (
    <div className="flex flex-col gap-1">
        <ThemedText size="xs" variant="secondary" className="font-medium">
            {label}
        </ThemedText>
        <ThemedText size="sm" className={bold ? "font-semibold" : ""}>
            {value}
        </ThemedText>
    </div>
);

export default PurchaseOrderDetail;
